// What to SAY about a chart that settled with an error. Pure, so the two
// classifications that matter can be tested: a chart whose warehouse object, or
// whose COLUMN, is behind this environment's pin is not broken, and the card
// must not read like a bug report.
//
// The backend settles such a chart with `warehouse_object_missing: <dataset.table>`
// or `warehouse_column_missing: <column>` (service/bi/core/warehouse_errors.py).
// The prefixes are the wire contract between the two; change one in both places
// or in neither.

export const WAREHOUSE_OBJECT_MISSING = "warehouse_object_missing";
export const WAREHOUSE_COLUMN_MISSING = "warehouse_column_missing";

export type ChartErrorKind = "behind_pin" | "column_behind_pin" | "timeout" | "generic";

export interface ChartErrorDescription {
  kind: ChartErrorKind;
  title: string;
  message: string;
  /** Whether a Retry button is worth offering. A table that is not there yet
   *  gives the same answer on every attempt until the pin moves. */
  retryable: boolean;
}

export function describeChartError(message: string, isTimeout: boolean): ChartErrorDescription {
  const behind = message.match(new RegExp(`^${WAREHOUSE_OBJECT_MISSING}:\\s*(\\S+)`));
  if (behind) {
    const object = behind[1];
    return {
      kind: "behind_pin",
      title: "Not in this environment's warehouse yet",
      message:
        `This chart reads ${object}, which this environment's warehouse has not built yet. ` +
        "Dashboards ship with the app; the views they read ship with the warehouse pin, which moves " +
        "once this environment's database can serve them. It lights up on its own when the pin passes " +
        "the commit that added the view. It already renders on dev.",
      retryable: false,
    };
  }
  // Deliberately two possibilities in one card. The service sees the same
  // BigQuery message whether the name is waiting on the pin or is simply
  // misspelled, so naming only the first would tell a reader to wait for a pin
  // that will never fix their typo. Dev is the discriminator, and it is the one
  // thing the reader can act on.
  //
  // "Usually a column" rather than "the column", because BigQuery says
  // `Unrecognized name` for ANY identifier it cannot resolve: nearly always a
  // column, occasionally a dropped CTE, a stale table alias or a renamed UDF.
  // The wire prefix keeps the `column` wording (it is a contract, and it is
  // right almost every time); the sentence a person reads must not assert what
  // the service did not check.
  const columnBehind = message.match(new RegExp(`^${WAREHOUSE_COLUMN_MISSING}:\\s*(\\S+)`));
  if (columnBehind) {
    const column = columnBehind[1];
    return {
      kind: "column_behind_pin",
      title: "That name is not in this environment's warehouse",
      message:
        `This chart reads ${column}, which does not resolve against this environment's warehouse. ` +
        "Usually that is a column, and two things look identical from here: the view may have gained " +
        "it in a commit the warehouse pin has not reached yet, or the name may be wrong. Dev is how " +
        "you tell them apart, because it tracks the newest definitions: a chart waiting on the pin " +
        "renders there, a wrong name renders nowhere.",
      retryable: false,
    };
  }
  if (isTimeout) {
    return { kind: "timeout", title: "Took too long", message, retryable: true };
  }
  return { kind: "generic", title: "Couldn't load this chart", message, retryable: true };
}
